import type { StaffRole } from '@planuze/admin-contracts';
import {
  Button,
  Page,
  PageActions,
  PageContent,
  PageDescription,
  PageHeader,
  PageTitle,
  PageTitleBlock,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '@planuze/ui';
import { useNavigate } from '@tanstack/react-router';
import { useState } from 'react';
import { PublicSection } from '../components/plans/public-section.js';
import { TargetedSection } from '../components/plans/targeted-section.js';
import { usePlansContent } from '../content/sections/plans.js';
import { useStaffSession } from '../hooks/use-staff-session.js';

const MANAGE_ROLES: readonly StaffRole[] = ['super_admin', 'billing_ops'];

type PlanTab = 'plans' | 'negotiated';

/* Orquestra as abas Planos/Negociados (ADR-0704). Cada aba é uma seção autocontida
   (toolbar de filtros + tabela + modal de edição). A CRIAÇÃO virou página cheia
   (plan-create-page/targeted-plan-create-page) — os botões do header navegam até elas em vez
   de abrir modal, porque criar dispara Product + Price no Stripe (irreversível). */
export function PlansPage() {
  const copy = usePlansContent();
  const navigate = useNavigate();
  const session = useStaffSession();
  const role = session?.role ?? 'readonly';
  const canManage = MANAGE_ROLES.includes(role);
  const [tab, setTab] = useState<PlanTab>('plans');

  return (
    <Page>
      <PageHeader>
        <PageTitleBlock>
          <PageTitle>{copy.pageTitle}</PageTitle>
          <PageDescription>{copy.pageDescription}</PageDescription>
        </PageTitleBlock>
        {canManage && (
          <PageActions>
            {tab === 'plans' ? (
              <Button onClick={() => navigate({ to: '/plans/new' }).catch(() => undefined)}>
                {copy.newPlanButton}
              </Button>
            ) : (
              <Button
                onClick={() => navigate({ to: '/plans/new-targeted' }).catch(() => undefined)}
              >
                {copy.newTargetedPlanButton}
              </Button>
            )}
          </PageActions>
        )}
      </PageHeader>
      <PageContent className="space-y-4">
        <Tabs value={tab} onValueChange={(value) => setTab(value as PlanTab)}>
          <TabsList>
            <TabsTrigger value="plans">{copy.plansTab}</TabsTrigger>
            <TabsTrigger value="negotiated">{copy.negotiatedTab}</TabsTrigger>
          </TabsList>
          <TabsContent value="plans" className="space-y-4">
            <PublicSection active={tab === 'plans'} canManage={canManage} />
          </TabsContent>
          <TabsContent value="negotiated" className="space-y-4">
            <TargetedSection active={tab === 'negotiated'} canManage={canManage} staffRole={role} />
          </TabsContent>
        </Tabs>
      </PageContent>
    </Page>
  );
}
